<script setup lang="ts">
import type { TemplateCardView } from '~/utils/template-catalog'

/**
 * One marketplace app, as a radio.
 *
 * The tile used to say the app's name, a category badge and its minimum specs —
 * and "Dify", "WAHA", "ntfy" or "GenieACS" mean nothing to most people buying a
 * server. The tagline says what the software DOES, and it is the radio's
 * accessible description, so a screen reader hears it with the name.
 *
 * Fit is silent: a card says nothing when the chosen plan runs the app, a
 * warning when it does not, and — while no plan is chosen, which is the normal
 * case now that the image is picked first — the minimum, so the plan step that
 * follows is not a surprise.
 *
 * One interactive element and nothing inside it, deliberately: a card that
 * opened details or linked somewhere would put a control inside a control.
 */
const props = withDefaults(defineProps<{
  view: TemplateCardView
  selected: boolean
  /** Hidden inside the app's own shelf, where it would repeat the heading. */
  showShelf?: boolean
  /** Highlights these words in the name (search results). */
  query?: string
  tabindex?: number
}>(), {
  showShelf: true,
  query: '',
  tabindex: 0,
})

const emit = defineEmits<{ select: [] }>()

const descriptionId = useId()
const nameSegments = computed(() =>
  props.query ? highlightSegments(props.view.template.name, props.query) : [{ text: props.view.template.name, match: false }]
)
</script>

<template>
  <button
    type="button"
    role="radio"
    data-testid="marketplace-app-tile"
    :data-template-code="view.code"
    :data-roving-id="view.code"
    :aria-checked="selected"
    :aria-describedby="descriptionId"
    :tabindex="tabindex"
    :class="[
      'group relative flex w-full items-start gap-3 rounded-xl border p-3 text-left transition',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
      selected
        ? 'border-primary bg-primary/5 ring-1 ring-primary on-tint dark:bg-primary/10'
        : 'border-default bg-default hover:border-accented hover:bg-elevated/40 hover:shadow-sm motion-safe:hover:-translate-y-px',
    ]"
    @click="emit('select')"
  >
    <TemplateLogo
      :icon-url="view.template.icon_url"
      :fallback="view.fallbackIcon"
      :accent="view.accent"
    />

    <span class="min-w-0 flex-1">
      <span class="flex min-w-0 items-center gap-1.5 pr-6">
        <span class="truncate text-sm font-semibold leading-5">
          <template v-for="(segment, index) in nameSegments" :key="index">
            <mark
              v-if="segment.match"
              class="rounded-[3px] bg-primary/15 text-inherit dark:bg-primary/25"
            >{{ segment.text }}</mark>
            <template v-else>{{ segment.text }}</template>
          </template>
        </span>
        <span v-if="view.version" class="shrink-0 text-xs text-muted">{{ view.version }}</span>
        <UBadge
          v-if="view.isNew"
          data-testid="template-new-badge"
          color="primary"
          variant="soft"
          size="sm"
          class="shrink-0"
        >
          {{ $t('billing.page.checkout.os.catalog.card.new') }}
        </UBadge>
      </span>

      <span :id="descriptionId" class="mt-0.5 line-clamp-2 text-xs leading-4 text-muted">
        {{ view.tagline }}
      </span>

      <span
        v-if="showShelf || view.shortfall || view.minimum"
        class="mt-1.5 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[11px] leading-4"
      >
        <span v-if="showShelf && view.groupLabel" class="text-muted">{{ view.groupLabel }}</span>
        <!-- Status ships as icon + words, never colour alone. -->
        <span v-if="view.shortfall" class="inline-flex items-center gap-1 font-medium text-warning-700 dark:text-warning-400">
          <UIcon name="i-lucide-alert-triangle" class="size-3" />
          {{ $t('billing.page.checkout.os.catalog.card.needs', { spec: specLabel(view.shortfall) }) }}
        </span>
        <span v-else-if="view.minimum" class="inline-flex items-center gap-1 text-muted">
          <UIcon name="i-lucide-memory-stick" class="size-3" />
          {{ $t('billing.page.checkout.os.catalog.card.minimum', { spec: view.minimum }) }}
        </span>
      </span>
    </span>

    <span class="absolute right-3 top-3" aria-hidden="true">
      <UIcon
        :name="selected ? 'i-lucide-circle-check-big' : 'i-lucide-circle'"
        :class="selected ? 'size-5 text-primary' : 'size-5 text-muted/50 group-hover:text-muted'"
      />
    </span>
  </button>
</template>
